import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, MapPin, Hotel, BookOpen, FileText, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { hotels, destinations } from '../data';
import { articles_da, articles_en } from '../pages/RejsetipsPage';
import { useLanguage } from './LanguageContext';
import { matchesHotelSearch } from '../lib/hotelFilters';

const staticPages_da = [
  {
    name: 'Forside',
    pageKey: 'home',
    description: 'Alora Rejser - Personlige og trygge rejser for kørestolsbrugere og gangbesværede.'
  },
  {
    name: 'Om Alora',
    pageKey: 'about',
    description: 'Læs historien bag Alora Rejser og vores personlige engagement i tilgængelige rejser.'
  },
  {
    name: 'Udvalgte rejser',
    pageKey: 'featured-trips',
    description: 'Oplev juletur til London, Disneyland Paris, New York, Østrig og Paris med tilgængelige rammer.'
  },
  {
    name: 'Priser',
    pageKey: 'prices',
    description: 'Se priser, pakkerejser og hvad der er inkluderet i din trygge ferie.'
  },
  {
    name: 'Kontakt',
    pageKey: 'contact',
    description: 'Kontakt os for personlig rådgivning eller spørgsmål om tilgængelighed.'
  },
  {
    name: 'Indhent tilbud',
    pageKey: 'quote',
    description: 'Få et personligt, uforpligtende og skræddersyet tilbud på din næste rejse.'
  },
  {
    name: 'Praktisk information',
    pageKey: 'practical',
    description: 'Nyttig viden om flyassistance, hjælpemidler, rettigheder og tryghed på rejsen.'
  },
  {
    name: 'Billedgalleri',
    pageKey: 'gallery',
    description: 'Se billeder fra vores handicapvenlige hoteller, værelser og rejsemål.'
  },
  {
    name: 'Rejsebetingelser',
    pageKey: 'terms',
    description: 'Læs vores betingelser for trygge og dækkede pakkerejser.'
  }
];

const staticPages_en = [
  {
    name: 'Home',
    pageKey: 'home',
    description: 'Alora Rejser - Personal and safe travel for wheelchair users and people with limited mobility.'
  },
  {
    name: 'About Alora',
    pageKey: 'about',
    description: 'Read the story behind Alora Rejser and our personal commitment to accessible travel.'
  },
  {
    name: 'Selected Trips',
    pageKey: 'featured-trips',
    description: 'Experience Christmas in London, Disneyland Paris, New York, Austria, and Paris with full accessibility.'
  },
  {
    name: 'Prices',
    pageKey: 'prices',
    description: 'See prices, package tours, and what is included in your safe holiday.'
  },
  {
    name: 'Contact',
    pageKey: 'contact',
    description: 'Contact us for personal advice or questions about accessibility.'
  },
  {
    name: 'Request Quote',
    pageKey: 'quote',
    description: 'Get a personal, non-binding, and customized offer for your next trip.'
  },
  {
    name: 'Practical Information',
    pageKey: 'practical',
    description: 'Useful knowledge about flight assistance, mobility aids, rights, and safety on your journey.'
  },
  {
    name: 'Photo Gallery',
    pageKey: 'gallery',
    description: 'See photos of our accessible hotels, rooms, and destinations.'
  },
  {
    name: 'Terms & Conditions',
    pageKey: 'terms',
    description: 'Read our terms and conditions for safe and covered package tours.'
  }
];

const GlobalSearch = ({ isOpen, onClose, initialQuery = '' }) => {
  const { language, translateObject, getLocalizedPath } = useLanguage();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState({
    destinations: [],
    hotels: [],
    articles: [],
    pages: []
  });
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const currentStaticPages = language === 'en' ? staticPages_en : staticPages_da;
  const currentArticles = language === 'en' ? articles_en : articles_da;

  // Auto-fokuser inputfeltet, når søgningen åbnes og synkroniser initialQuery
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      // Forhindr scroll på baggrunden, når søgningen er åben
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialQuery]);

  // Håndter Esc-tast til at lukke søgningen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Udfør søgningen i realtid
  useEffect(() => {
    if (!query.trim()) {
      setResults({ destinations: [], hotels: [], articles: [], pages: [] });
      return;
    }

    const lowerQuery = query.toLowerCase();

    // 1. Søg i destinationer (translated)
    const matchedDestinations = destinations
      .map((dest) => translateObject(dest))
      .filter(
        (dest) =>
          dest.title.toLowerCase().includes(lowerQuery) ||
          dest.description.toLowerCase().includes(lowerQuery) ||
          dest.longDescription.toLowerCase().includes(lowerQuery) ||
          dest.features.some((f) => f.toLowerCase().includes(lowerQuery))
      );

    // 2. Søg i hoteller (translated)
    const matchedHotels = hotels
      .map((hotel) => translateObject(hotel))
      .filter((hotel) => {
        const dest = destinations.find((d) => d.id === hotel.destinationId);
        const translatedDest = translateObject(dest);
        const destTitle = translatedDest ? translatedDest.title : '';
        return matchesHotelSearch(hotel, query, destTitle);
      });

    // 3. Søg i rejsetips-artikler
    const matchedArticles = currentArticles.filter(
      (article) =>
        article.title.toLowerCase().includes(lowerQuery) ||
        article.intro.toLowerCase().includes(lowerQuery) ||
        article.category.toLowerCase().includes(lowerQuery)
    );

    // 4. Søg i statiske sider
    const matchedPages = currentStaticPages.filter(
      (page) =>
        page.name.toLowerCase().includes(lowerQuery) ||
        page.description.toLowerCase().includes(lowerQuery)
    );

    setResults({
      destinations: matchedDestinations,
      hotels: matchedHotels,
      articles: matchedArticles,
      pages: matchedPages
    });
  }, [query, language, currentArticles, currentStaticPages]);

  const handleItemClick = (path) => {
    navigate(path);
    onClose();
    setQuery('');
  };

  const hasResults =
    results.destinations.length > 0 ||
    results.hotels.length > 0 ||
    results.articles.length > 0 ||
    results.pages.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-start pt-[10vh] px-4 md:px-6">
          {/* Mørk sløret baggrund */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-950/50 backdrop-blur-md"
          ></motion.div>

          {/* Søgeboks */}
          <motion.div
            initial={{ opacity: 0, y: -30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.98 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-stone-100 overflow-hidden flex flex-col max-h-[75vh] z-10"
          >
            {/* Søgefelt Header */}
            <div className="flex items-center px-6 py-5 border-b border-stone-100">
              <Search className="text-[#99A18C] w-6 h-6 mr-4 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder={
                  language === 'en'
                    ? "Search the entire website (e.g., Mallorca, assistance, prices...)"
                    : "Søg på hele hjemmesiden (f.eks. Mallorca, assistance, priser...)"
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full text-base md:text-lg text-slate-800 placeholder-slate-400 focus:outline-none bg-transparent"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 text-slate-400 hover:text-slate-600 mr-2 transition-colors"
                >
                  <X size={18} />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 bg-stone-50 hover:bg-stone-100 text-slate-500 hover:text-slate-700 rounded-xl transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Søgeresultater */}
            <div className="overflow-y-auto p-6 space-y-6 no-scrollbar">
              {!query.trim() ? (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-medium">
                    {language === 'en' ? 'What are you looking for?' : 'Hvad søger du efter?'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                    {language === 'en'
                      ? "Enter a search term to search across all our destinations, hotels, travel tips, and information pages."
                      : "Indtast et søgeord for at søge på tværs af alle vores rejsemål, hoteller, rejsetips og informationssider."}
                  </p>
                </div>
              ) : hasResults ? (
                <div className="space-y-6">
                  {/* Destinationer */}
                  {results.destinations.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#737C66] mb-3 flex items-center gap-1.5">
                        <MapPin size={14} />
                        {language === 'en' ? 'Destinations' : 'Rejsemål'} ({results.destinations.length})
                      </h3>
                      <div className="space-y-2">
                        {results.destinations.map((dest) => (
                          <div
                            key={dest.id}
                            onClick={() => handleItemClick(getLocalizedPath('destination', language, { id: dest.id }))}
                            className="p-3.5 rounded-2xl hover:bg-stone-50 border border-transparent hover:border-stone-100 flex items-center justify-between cursor-pointer group transition-all"
                          >
                            <div className="flex-1 min-w-0 pr-4">
                              <h4 className="font-serif font-bold text-slate-800 group-hover:text-[#737C66] transition-colors">
                                {dest.title}
                              </h4>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                {dest.description}
                              </p>
                            </div>
                            <ArrowRight size={16} className="text-slate-300 group-hover:text-[#99A18C] group-hover:translate-x-1 transition-all flex-shrink-0" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hoteller */}
                  {results.hotels.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#737C66] mb-3 flex items-center gap-1.5">
                        <Hotel size={14} />
                        {language === 'en' ? 'Hotels' : 'Hoteller'} ({results.hotels.length})
                      </h3>
                      <div className="space-y-2">
                        {results.hotels.map((hotel) => (
                          <div
                            key={hotel.id}
                            onClick={() => handleItemClick(getLocalizedPath('hotel', language, { hotelId: hotel.id }))}
                            className="p-3.5 rounded-2xl hover:bg-stone-50 border border-transparent hover:border-stone-100 flex items-center justify-between cursor-pointer group transition-all"
                          >
                            <div className="flex-1 min-w-0 pr-4">
                              <div className="flex items-center gap-2">
                                <h4 className="font-serif font-bold text-slate-800 group-hover:text-[#737C66] transition-colors">
                                  {hotel.name}
                                </h4>
                                <span className="text-[10px] bg-stone-100 text-slate-500 px-2 py-0.5 rounded-full">
                                  {hotel.area}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                {hotel.description}
                              </p>
                            </div>
                            <ArrowRight size={16} className="text-slate-300 group-hover:text-[#99A18C] group-hover:translate-x-1 transition-all flex-shrink-0" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rejsetips & Artikler */}
                  {results.articles.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#737C66] mb-3 flex items-center gap-1.5">
                        <BookOpen size={14} />
                        {language === 'en' ? 'Travel Tips & Guides' : 'Rejsetips & Guides'} ({results.articles.length})
                      </h3>
                      <div className="space-y-2">
                        {results.articles.map((article) => {
                          const articlePath = article.routePath || `${getLocalizedPath('traveltips', language)}?article=${article.id}`;
                          return (
                            <div
                              key={article.id}
                              onClick={() => handleItemClick(articlePath)}
                              className="p-3.5 rounded-2xl hover:bg-stone-50 border border-transparent hover:border-stone-100 flex items-center justify-between cursor-pointer group transition-all"
                            >
                              <div className="flex-1 min-w-0 pr-4">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-serif font-bold text-slate-800 group-hover:text-[#737C66] transition-colors line-clamp-1">
                                    {article.title}
                                  </h4>
                                  <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full flex-shrink-0">
                                    {article.category}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                  {article.intro}
                                </p>
                              </div>
                              <ArrowRight size={16} className="text-slate-300 group-hover:text-[#99A18C] group-hover:translate-x-1 transition-all flex-shrink-0" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Sider */}
                  {results.pages.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#737C66] mb-3 flex items-center gap-1.5">
                        <FileText size={14} />
                        {language === 'en' ? 'Pages' : 'Sider'} ({results.pages.length})
                      </h3>
                      <div className="space-y-2">
                        {results.pages.map((page) => (
                          <div
                            key={page.pageKey}
                            onClick={() => handleItemClick(getLocalizedPath(page.pageKey, language))}
                            className="p-3.5 rounded-2xl hover:bg-stone-50 border border-transparent hover:border-stone-100 flex items-center justify-between cursor-pointer group transition-all"
                          >
                            <div className="flex-1 min-w-0 pr-4">
                              <h4 className="font-serif font-bold text-slate-800 group-hover:text-[#737C66] transition-colors">
                                {page.name}
                              </h4>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                {page.description}
                              </p>
                            </div>
                            <ArrowRight size={16} className="text-slate-300 group-hover:text-[#99A18C] group-hover:translate-x-1 transition-all flex-shrink-0" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p className="text-sm font-medium">
                    {language === 'en' ? `No results found for "${query}"` : `Ingen resultater fundet for "${query}"`}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {language === 'en'
                      ? "Try searching for something else or check for typos."
                      : "Prøv at søge efter noget andet eller tjek for stavefejl."}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GlobalSearch;
